import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  commentsFailureOf,
  commentsLoadingMore,
  commentsPageFailed,
  commentsPageLoaded,
  commentsSortFallback,
  commentsSortSelected,
  continuationQuery,
  firstPageQuery,
  initialCommentsState,
  repliesFailed,
  repliesLoaded,
  repliesLoading,
  toggleReplies,
  type CommentPage,
  type CommentQuery,
  type CommentsFailure,
  type CommentSort,
  type CommentsState,
} from '@visious/core';
import { fetchCommentPage } from '../../api/comments';
import type { ApiProblem } from '../../api/videos';
import { systemClock } from '../../platform/clock';
import { getPlatform } from '../../platform/instance';
import { toCommentsView, type CommentsView } from './comments';

/**
 * The comment section's React binding: the fetching, and nothing else. Every transition it applies
 * comes from core's `comments/threads.ts`, so what is left here is the platform half — effects,
 * a mounted flag, and the sequencing of one request after another.
 *
 * **This is the one screen surface that does not use TanStack Query, and that is deliberate.**
 * Query is in the project for the cache, the de-duplication and the lifecycle (§4/R13), and it
 * models none of the three things this list does: pages that *append* under one identity, a second
 * paged list per expanded thread (a hook cannot be called in a loop, and R13 forbids putting the
 * fetch in the `.tsx` that renders the rows), and a sort that can be substituted mid-flight when
 * the instance refuses the one asked for. Bolting those onto a query cache gives the list two
 * owners, which is the defect class 0e already paid for — a poll that retracted what another
 * observer had recorded. One reducer, one owner.
 */

export interface CommentsHandlers {
  readonly setSort: (sort: CommentSort) => void;
  readonly loadMore: () => void;
  readonly toggleReplies: (commentId: string) => void;
  readonly loadMoreReplies: (commentId: string) => void;
  readonly retry: () => void;
}

export interface CommentsBinding {
  readonly view: CommentsView;
  readonly handlers: CommentsHandlers;
}

type PageResult = { readonly ok: true; readonly page: CommentPage } | { readonly ok: false; readonly failure: CommentsFailure };

/** Every failure the fetch can produce, reduced to the three the list has different words for. */
function failureOf(problem: ApiProblem): CommentsFailure {
  if (problem.kind === 'transport') return commentsFailureOf(problem.error);
  // A malformed body or an unparseable page is not retryable and not "no comments" — it is the
  // instance answering with something else, which is the third arm.
  return { kind: 'error', retryable: problem.kind === 'malformed-json' };
}

async function loadPage(query: CommentQuery): Promise<PageResult> {
  const boot = await getPlatform();
  if (!boot.ok) return { ok: false, failure: { kind: 'error', retryable: false } };

  const result = await fetchCommentPage(boot.platform, query);
  return result.ok ? { ok: true, page: result.value } : { ok: false, failure: failureOf(result.error) };
}

export interface CommentsOptions {
  /** Whether the section is on screen. Nothing fetches while false; the first Show does. */
  readonly enabled: boolean;
  /** Only names the reason a live stream has no comments; it does not suppress the request. */
  readonly live: boolean;
}

export function useComments(videoId: string, options: CommentsOptions): CommentsBinding {
  const { enabled, live } = options;
  const [state, setState] = useState<CommentsState>(() => initialCommentsState(videoId));
  /**
   * Read by every async continuation so it can check what it is landing on. State would be a stale
   * closure by then — a reply page that arrives after the reader switched sort must not be applied
   * to the list that replaced it.
   */
  const stateRef = useRef(state);
  const aliveRef = useRef(true);
  /** Bumped by `retry()` and by a sort change, so the loading effect re-runs on a fresh list. */
  const [attempt, setAttempt] = useState(0);

  const [nowEpochSec] = useState(() => Math.floor(systemClock.nowEpochMs() / 1000));

  /**
   * The ref is the state and `setState` is the mirror React renders from — not the other way round.
   * Computing the next value here rather than inside a `setState` updater keeps the updater out of
   * it entirely, which matters because React may invoke an updater twice and every transition here
   * is called from an `await` continuation that has to read the *current* list, not the one that
   * was current when it started.
   */
  const apply = useCallback((update: (previous: CommentsState) => CommentsState) => {
    if (!aliveRef.current) return;
    const next = update(stateRef.current);
    if (next === stateRef.current) return;
    stateRef.current = next;
    setState(next);
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // A different video is a different list. Resetting here rather than keying the component means the
  // player above does not remount with it.
  const requestedSort = state.requestedSort;
  useEffect(() => {
    apply((previous) =>
      previous.videoId === videoId ? previous : initialCommentsState(videoId, previous.requestedSort),
    );
  }, [apply, videoId]);

  useEffect(() => {
    if (!enabled) return;
    /*
     * Only an unanswered first page fetches: `enabled` follows the section's own Show/Hide,
     * so it can flip back to true long after the list loaded — re-showing must not throw a loaded
     * list away for a fresh page one. Every path that *wants* a refetch (retry, a sort change, a
     * new video) already resets the state to `loading` before bumping this effect.
     */
    if (stateRef.current.videoId === videoId && stateRef.current.phase !== 'loading') return;
    let cancelled = false;

    void (async () => {
      const first = await loadPage(firstPageQuery(videoId, requestedSort));
      if (cancelled) return;

      if (first.ok) {
        apply((previous) => commentsPageLoaded(previous, first.page, { append: false, sort: requestedSort }));
        return;
      }

      // The slice's decision, at its one call site: `sort_by=top` is refused for a large minority
      // of videos and `sort_by=new` answers for all of them (API reference §6).
      const fallback = commentsSortFallback(requestedSort, first.failure);
      if (fallback === null) {
        apply((previous) => commentsPageFailed(previous, first.failure, requestedSort));
        return;
      }

      const rescued = await loadPage(firstPageQuery(videoId, fallback));
      if (cancelled) return;
      apply((previous) =>
        rescued.ok
          ? commentsPageLoaded(previous, rescued.page, { append: false, sort: fallback })
          : // Report the *first* failure: "top is broken" is what happened, and the reader is
            // owed that rather than a message about an order they never asked for.
            commentsPageFailed(previous, first.failure, fallback),
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [apply, attempt, enabled, requestedSort, videoId]);

  const loadMore = useCallback(() => {
    const current = stateRef.current;
    if (current.continuation === null || current.loadingMore || current.phase !== 'ready') return;
    const token = current.continuation;

    apply(commentsLoadingMore);
    void (async () => {
      const result = await loadPage(continuationQuery(videoId, token));
      apply((previous) =>
        // The token identifies the page, so a stale response is one whose token is no longer the
        // one being waited on — after a sort change, that is every response still in flight.
        previous.continuation !== token
          ? previous
          : result.ok
            ? commentsPageLoaded(previous, result.page, { append: true, sort: previous.sort })
            : commentsPageFailed(previous, result.failure, previous.sort),
      );
    })();
  }, [apply, videoId]);

  const fetchReplies = useCallback(
    (commentId: string, token: string) => {
      void (async () => {
        const result = await loadPage(continuationQuery(videoId, token));
        apply((previous) => {
          const thread = previous.threads.find((candidate) => candidate.comment.commentId === commentId);
          // The thread was collapsed, or the list was replaced, while this was in flight.
          if (thread === undefined || thread.status !== 'loading') return previous;
          return result.ok ? repliesLoaded(previous, commentId, result.page) : repliesFailed(previous, commentId);
        });
      })();
    },
    [apply, videoId],
  );

  const handlers: CommentsHandlers = useMemo(
    () => ({
      setSort: (sort: CommentSort) => {
        const next = commentsSortSelected(stateRef.current, sort);
        if (next === stateRef.current) return;
        apply(() => next);
        setAttempt((previous) => previous + 1);
      },

      loadMore,

      toggleReplies: (commentId: string) => {
        const { state: next, fetch } = toggleReplies(stateRef.current, commentId);
        apply(() => next);
        if (fetch === null) return;
        fetchReplies(commentId, fetch);
      },

      /** The "show more replies" button — an expansion that is already open, one page deeper. */
      loadMoreReplies: (commentId: string) => {
        const thread = stateRef.current.threads.find((candidate) => candidate.comment.commentId === commentId);
        if (thread === undefined || thread.continuation === null || thread.status === 'loading') return;
        const token = thread.continuation;
        apply((previous) => repliesLoading(previous, commentId));
        fetchReplies(commentId, token);
      },

      retry: () => {
        apply((previous) => initialCommentsState(previous.videoId, previous.requestedSort));
        setAttempt((previous) => previous + 1);
      },
    }),
    [apply, fetchReplies, loadMore],
  );

  const view = useMemo(() => toCommentsView(state, { nowEpochSec, live }), [live, nowEpochSec, state]);

  return { view, handlers };
}
